import { useState } from "react";
import {
  Palette,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  ShieldAlert,
  Sparkles,
  Copy,
  Check,
  Eye,
  Sliders,
  Layers,
  ArrowRight,
  Sun,
  Moon,
  HelpCircle,
  Clock,
  Ban,
  FileText,
  TrendingUp,
} from "lucide-react";
import {
  Badge,
  Button,
  KpiCard,
  MOBILE_KPI_TILE_STYLES,
  resolveKpiTone,
  type SemanticTone,
} from "@/shared/ui";
import { cn } from "@/shared/lib/cn";
import { TestUiUnifiedStatusMatrix } from "./TestUiUnifiedStatusMatrix";

interface ColorTokenItem {
  name: string;
  variable: string;
  tailwindClass: string;
  cssValue: string;
  previewBg: string;
  textColor: string;
  role: string;
  usage: string;
  level: "soft" | "solid" | "foreground" | "border" | "hover";
}

interface ColorFamily {
  id: string;
  name: string;
  vietnameseName: string;
  description: string;
  hue: string;
  badgeTone: SemanticTone;
  tokens: ColorTokenItem[];
}

const COLOR_FAMILIES: ColorFamily[] = [
  {
    id: "primary",
    name: "Primary (Brand)",
    vietnameseName: "Xanh TM Food (Hue 152)",
    description: "Màu sắc thương hiệu chủ đạo — tượng trưng cho nông sản sạch, thiên nhiên và tiêu chuẩn nhà máy hiện đại.",
    hue: "oklch(152°)",
    badgeTone: "primary",
    tokens: [
      {
        name: "Primary Soft",
        variable: "--color-primary-soft",
        tailwindClass: "bg-primary-soft",
        cssValue: "oklch(94% 0.03 152)",
        previewBg: "oklch(94% 0.03 152)",
        textColor: "oklch(28% 0.08 152)",
        role: "Nền nhạt (Level 1)",
        usage: "Nền Badge trạng thái, hover nhẹ trên hàng bảng, highlight thẻ active",
        level: "soft",
      },
      {
        name: "Primary Solid",
        variable: "--color-primary",
        tailwindClass: "bg-primary text-on-primary",
        cssValue: "oklch(42% 0.09 152)",
        previewBg: "oklch(42% 0.09 152)",
        textColor: "oklch(99% 0.002 90)",
        role: "Nhấn mạnh (Level 2 - Base)",
        usage: "Nút Primary CTA, Tab active indicator, icon tròn nổi bật",
        level: "solid",
      },
      {
        name: "Primary Hover",
        variable: "--color-primary-hover",
        tailwindClass: "bg-primary-hover",
        cssValue: "oklch(36% 0.085 152)",
        previewBg: "oklch(36% 0.085 152)",
        textColor: "oklch(99% 0.002 90)",
        role: "Tương tác (Hover)",
        usage: "Trạng thái di chuột của nút chính, hiệu ứng nhấn",
        level: "hover",
      },
      {
        name: "Primary Foreground",
        variable: "--color-primary-fg",
        tailwindClass: "text-primary-fg",
        cssValue: "oklch(28% 0.08 152)",
        previewBg: "oklch(28% 0.08 152)",
        textColor: "white",
        role: "Chữ tương phản (Level 3)",
        usage: "Chữ & Icon đặt trên nền Soft. Đạt tương phản cao ≥ 7:1",
        level: "foreground",
      },
    ],
  },
  {
    id: "accent",
    name: "Accent (Đang SX / WIP / Tiến độ)",
    vietnameseName: "Xanh cốm tươi Lime (Hue 126)",
    description: "Biểu thị các lệnh sản xuất đang chạy, tiến trình WIP tại phân xưởng, pips hoạt động tức thời.",
    hue: "oklch(126°)",
    badgeTone: "accent",
    tokens: [
      {
        name: "Accent Soft",
        variable: "--color-accent-soft",
        tailwindClass: "bg-accent-soft",
        cssValue: "oklch(95% 0.045 126)",
        previewBg: "oklch(95% 0.045 126)",
        textColor: "oklch(32% 0.09 126)",
        role: "Nền nhạt (Level 1)",
        usage: "Nền badge 'Đang sản xuất', 'Đang xử lý', thẻ tiến trình đang chạy",
        level: "soft",
      },
      {
        name: "Accent Solid",
        variable: "--color-accent",
        tailwindClass: "bg-accent text-on-accent",
        cssValue: "oklch(80% 0.16 126)",
        previewBg: "oklch(80% 0.16 126)",
        textColor: "oklch(24% 0.01 90)",
        role: "Nhấn mạnh (Level 2 - Base)",
        usage: "Dot trạng thái đang chạy, thanh tiến độ phân đoạn active",
        level: "solid",
      },
      {
        name: "Accent Foreground",
        variable: "--color-accent-fg",
        tailwindClass: "text-accent-fg",
        cssValue: "oklch(32% 0.09 126)",
        previewBg: "oklch(32% 0.09 126)",
        textColor: "white",
        role: "Chữ tương phản (Level 3)",
        usage: "Chữ & icon tiến độ đặt trên nền Accent Soft",
        level: "foreground",
      },
    ],
  },
  {
    id: "success",
    name: "Success (Thành công)",
    vietnameseName: "Xanh lá tích cực (Hue 145)",
    description: "Biểu thị lệnh SX hoàn tất, nguyên liệu Đạt QC, kho Khả dụng, ký số thành công.",
    hue: "oklch(145°)",
    badgeTone: "success",
    tokens: [
      {
        name: "Success Soft",
        variable: "--color-success-soft",
        tailwindClass: "bg-success-soft",
        cssValue: "oklch(93% 0.05 145)",
        previewBg: "oklch(93% 0.05 145)",
        textColor: "oklch(30% 0.1 145)",
        role: "Nền nhạt (Level 1)",
        usage: "Nền badge 'Đạt', 'Hoàn tất', banner thành công",
        level: "soft",
      },
      {
        name: "Success Solid",
        variable: "--color-success",
        tailwindClass: "bg-success text-white",
        cssValue: "oklch(52% 0.13 145)",
        previewBg: "oklch(52% 0.13 145)",
        textColor: "white",
        role: "Nhấn mạnh (Level 2 - Base)",
        usage: "Icon check hoàn thành, chấm tròn online, thanh tiến độ 100%",
        level: "solid",
      },
      {
        name: "Success Foreground",
        variable: "--color-success-fg",
        tailwindClass: "text-success-fg",
        cssValue: "oklch(30% 0.1 145)",
        previewBg: "oklch(30% 0.1 145)",
        textColor: "white",
        role: "Chữ tương phản (Level 3)",
        usage: "Chữ & icon trên nền soft, chỉ số KPI tăng trưởng dương",
        level: "foreground",
      },
    ],
  },
  {
    id: "warning",
    name: "Warning (Cảnh báo)",
    vietnameseName: "Hổ phách / Vàng cam (Hue 70-75)",
    description: "Nhắc nhở người vận hành: Lô chờ kiểm tra, đơn hàng đang duyệt, tồn kho chạm ngưỡng tối thiểu.",
    hue: "oklch(70-75°)",
    badgeTone: "warning",
    tokens: [
      {
        name: "Warning Soft",
        variable: "--color-warning-soft",
        tailwindClass: "bg-warning-soft",
        cssValue: "oklch(93.5% 0.06 75)",
        previewBg: "oklch(93.5% 0.06 75)",
        textColor: "oklch(40% 0.12 70)",
        role: "Nền nhạt (Level 1)",
        usage: "Nền badge 'Chờ duyệt', 'Tạm giữ QC', cảnh báo hạn dùng",
        level: "soft",
      },
      {
        name: "Warning Solid",
        variable: "--color-warning",
        tailwindClass: "bg-warning text-text",
        cssValue: "oklch(68% 0.15 70)",
        previewBg: "oklch(68% 0.15 70)",
        textColor: "oklch(20% 0.02 70)",
        role: "Nhấn mạnh (Level 2 - Base)",
        usage: "Biểu tượng tam giác cảnh báo, dot trạng thái đang chờ",
        level: "solid",
      },
      {
        name: "Warning Foreground",
        variable: "--color-warning-fg",
        tailwindClass: "text-warning-fg",
        cssValue: "oklch(40% 0.12 70)",
        previewBg: "oklch(40% 0.12 70)",
        textColor: "white",
        role: "Chữ tương phản (Level 3)",
        usage: "Chữ hiển thị trên nền warning soft, tránh bị lóa dưới ánh đèn xưởng",
        level: "foreground",
      },
    ],
  },
  {
    id: "danger",
    name: "Danger & Hold (Nguy cấp / Khóa)",
    vietnameseName: "Đỏ đậm cảnh báo (Hue 25)",
    description: "Tuyệt đối tuân thủ HACCP: Lô hàng hỏng, vi phạm CCP, điểm kiểm soát tới hạn, lỗi hệ thống, từ chối duyệt.",
    hue: "oklch(25°)",
    badgeTone: "danger",
    tokens: [
      {
        name: "Danger Soft",
        variable: "--color-danger-soft",
        tailwindClass: "bg-danger-soft",
        cssValue: "oklch(93% 0.07 25)",
        previewBg: "oklch(93% 0.07 25)",
        textColor: "oklch(42% 0.17 25)",
        role: "Nền nhạt (Level 1)",
        usage: "Nền badge 'Không đạt', 'Khóa lô', 'Quá hạn', Banner báo lỗi",
        level: "soft",
      },
      {
        name: "Danger Solid",
        variable: "--color-danger",
        tailwindClass: "bg-danger text-white",
        cssValue: "oklch(53% 0.19 25)",
        previewBg: "oklch(53% 0.19 25)",
        textColor: "white",
        role: "Nhấn mạnh (Level 2 - Base)",
        usage: "Nút hủy / xóa nguy hiểm, icon CCP vi phạm, dot lỗi",
        level: "solid",
      },
      {
        name: "Danger Hover",
        variable: "--color-danger-hover",
        tailwindClass: "bg-danger-hover",
        cssValue: "oklch(45% 0.18 25)",
        previewBg: "oklch(45% 0.18 25)",
        textColor: "white",
        role: "Tương tác (Hover)",
        usage: "Trạng thái di chuột nút xóa / từ chối",
        level: "hover",
      },
      {
        name: "Danger Foreground",
        variable: "--color-danger-fg",
        tailwindClass: "text-danger-fg",
        cssValue: "oklch(42% 0.17 25)",
        previewBg: "oklch(42% 0.17 25)",
        textColor: "white",
        role: "Chữ tương phản (Level 3)",
        usage: "Chữ lỗi hiển thị dưới input, văn bản báo từ chối",
        level: "foreground",
      },
    ],
  },
  {
    id: "info",
    name: "Info (Thông tin & Điều hướng)",
    vietnameseName: "Xanh biển nhạt (Hue 195)",
    description: "Cung cấp ghi chú nghiệp vụ, trạng thái nháp (Draft), chuyển tiếp quy trình hoặc chỉ dẫn tài liệu.",
    hue: "oklch(195°)",
    badgeTone: "info",
    tokens: [
      {
        name: "Info Soft",
        variable: "--color-info-soft",
        tailwindClass: "bg-info-soft",
        cssValue: "oklch(93% 0.05 195)",
        previewBg: "oklch(93% 0.05 195)",
        textColor: "oklch(32% 0.07 195)",
        role: "Nền nhạt (Level 1)",
        usage: "Nền banner hướng dẫn, badge trạng thái 'Nháp / Lưu trữ'",
        level: "soft",
      },
      {
        name: "Info Solid",
        variable: "--color-info",
        tailwindClass: "bg-info text-text",
        cssValue: "oklch(80% 0.07 195)",
        previewBg: "oklch(80% 0.07 195)",
        textColor: "oklch(24% 0.01 90)",
        role: "Nhấn mạnh (Level 2 - Base)",
        usage: "Icon thông tin, liên kết tài liệu hướng dẫn vận hành",
        level: "solid",
      },
      {
        name: "Info Foreground",
        variable: "--color-info-fg",
        tailwindClass: "text-info-fg",
        cssValue: "oklch(32% 0.07 195)",
        previewBg: "oklch(32% 0.07 195)",
        textColor: "white",
        role: "Chữ tương phản (Level 3)",
        usage: "Chữ & icon thông tin trên nền soft",
        level: "foreground",
      },
    ],
  },
  {
    id: "hold",
    name: "Hold (Tạm giữ QC / Khóa CCP)",
    vietnameseName: "Đỏ niêm phong / Tạm giữ (Hold / CCP)",
    description: "Lô hàng bị niêm phong, giữ lại kiểm tra QC, hoặc vi phạm ngưỡng an toàn CCP (kế thừa bảng màu Danger theo tiêu chuẩn DS).",
    hue: "oklch(25°)",
    badgeTone: "hold",
    tokens: [
      {
        name: "Hold Soft",
        variable: "--color-hold-soft",
        tailwindClass: "bg-danger-soft",
        cssValue: "oklch(93% 0.07 25)",
        previewBg: "oklch(93% 0.07 25)",
        textColor: "oklch(42% 0.17 25)",
        role: "Nền nhạt (Level 1)",
        usage: "Nền badge 'Tạm giữ QC', 'Niêm phong lô', 'Chờ xử lý vi phạm CCP'",
        level: "soft",
      },
      {
        name: "Hold Solid",
        variable: "--color-hold",
        tailwindClass: "bg-danger text-white",
        cssValue: "oklch(53% 0.19 25)",
        previewBg: "oklch(53% 0.19 25)",
        textColor: "white",
        role: "Nhấn mạnh (Level 2 - Base)",
        usage: "Icon niêm phong, viền khóa lô hàng khẩn cấp",
        level: "solid",
      },
      {
        name: "Hold Foreground",
        variable: "--color-hold-fg",
        tailwindClass: "text-danger-fg",
        cssValue: "oklch(42% 0.17 25)",
        previewBg: "oklch(42% 0.17 25)",
        textColor: "white",
        role: "Chữ tương phản (Level 3)",
        usage: "Chữ cảnh báo niêm phong và lý do giữ lô trên nền Soft",
        level: "foreground",
      },
    ],
  },
  {
    id: "neutral",
    name: "Neutral (Cấu trúc & Nền)",
    vietnameseName: "Xám trung tính (Hue 250)",
    description: "Bộ khung nền chống mỏi mắt cho công nhân và chuyên viên vận hành trong ca 8-12 tiếng.",
    hue: "oklch(250°)",
    badgeTone: "neutral",
    tokens: [
      {
        name: "App Background",
        variable: "--color-bg",
        tailwindClass: "bg-bg",
        cssValue: "#f1f3f5",
        previewBg: "#f1f3f5",
        textColor: "oklch(23% 0.01 250)",
        role: "Nền ứng dụng (Canvas)",
        usage: "Xám trắng dịu mắt (~#f1f3f5), triệt tiêu chói lóa màn hình",
        level: "soft",
      },
      {
        name: "Surface",
        variable: "--color-surface",
        tailwindClass: "bg-surface",
        cssValue: "oklch(100% 0.002 95)",
        previewBg: "#ffffff",
        textColor: "oklch(23% 0.01 250)",
        role: "Mặt thẻ / Bảng (Card)",
        usage: "Nền thẻ card, modal, hàng bảng, dropdown",
        level: "soft",
      },
      {
        name: "Border Subtle",
        variable: "--color-border-subtle",
        tailwindClass: "border-border-subtle",
        cssValue: "oklch(90.5% 0.004 250)",
        previewBg: "oklch(90.5% 0.004 250)",
        textColor: "oklch(23% 0.01 250)",
        role: "Đường viền mỏng",
        usage: "Đường kẻ phân tách nhẹ trong bảng và card",
        level: "border",
      },
      {
        name: "Border Standard",
        variable: "--color-border",
        tailwindClass: "border-border",
        cssValue: "oklch(86.5% 0.006 250)",
        previewBg: "oklch(86.5% 0.006 250)",
        textColor: "oklch(23% 0.01 250)",
        role: "Đường viền chuẩn",
        usage: "Viền bao quanh Card, Input, Modal, Table",
        level: "border",
      },
      {
        name: "Text Primary",
        variable: "--color-text",
        tailwindClass: "text-text",
        cssValue: "oklch(23% 0.01 250)",
        previewBg: "oklch(23% 0.01 250)",
        textColor: "white",
        role: "Chữ chính",
        usage: "Tiêu đề, dữ liệu quan trọng, số liệu chính (Contrast > 12:1)",
        level: "foreground",
      },
      {
        name: "Text Secondary",
        variable: "--color-text-secondary",
        tailwindClass: "text-text-secondary",
        cssValue: "oklch(46% 0.01 250)",
        previewBg: "oklch(46% 0.01 250)",
        textColor: "white",
        role: "Chữ phụ",
        usage: "Nhãn trường dữ liệu, thông tin bổ trợ",
        level: "foreground",
      },
    ],
  },
];

const SEMANTIC_TONE_RULES: {
  tone: SemanticTone;
  title: string;
  ruleTitle: string;
  ruleDesc: string;
  keywords: string[];
  kpiDemo: {
    label: string;
    value: string;
    hint: string;
    badge: string;
    icon: any;
  };
}[] = [
  {
    tone: "neutral",
    title: "1. Neutral (Trung tính & Xám)",
    ruleTitle: "Quy tắc Vàng: Tất cả / Tổng & Chờ / Nháp",
    ruleDesc: "Bắt buộc dùng cho 'Tổng số', 'Tất cả phiếu' (trung tính, không thiên kiến) và các trạng thái 'Chờ duyệt', 'Bản ghi nháp', 'Tạm lưu', 'Không hoạt động' (màu xám dịu mắt, tránh gây xao nhãng).",
    keywords: ["all", "total", "tất cả", "tổng", "pending", "draft", "chờ", "nháp", "tạm lưu", "đang tắt"],
    kpiDemo: {
      label: "Tổng phiếu / Chờ duyệt",
      value: "158",
      hint: "Bao gồm 12 bản ghi nháp",
      badge: "Toàn bộ",
      icon: Layers,
    },
  },
  {
    tone: "primary",
    title: "2. Primary (Thương hiệu TM Food)",
    ruleTitle: "Quy tắc: Nhận diện thương hiệu & Hành động chính",
    ruleDesc: "Dành riêng cho nút Brand CTA, logo/header, tab active indicator hoặc thẻ danh mục tiêu điểm thương hiệu.",
    keywords: ["primary", "brand", "chính", "thương hiệu", "tiêu điểm"],
    kpiDemo: {
      label: "Đạt chuẩn ISO Brand",
      value: "99.4%",
      hint: "Tiêu chuẩn xuất khẩu",
      badge: "TM Food",
      icon: Sparkles,
    },
  },
  {
    tone: "accent",
    title: "3. Accent (Xanh cốm tươi WIP)",
    ruleTitle: "Quy tắc: Đang sản xuất / Đang chạy / Tiến trình thực",
    ruleDesc: "Biểu thị các lệnh sản xuất đang chạy máy, phân xưởng đang gom hàng, hoặc tiến trình công việc đang diễn ra thực tế.",
    keywords: ["inProgress", "running", "đang chạy", "đang sx", "đang sản xuất", "wip"],
    kpiDemo: {
      label: "Đang sản xuất (WIP)",
      value: "34",
      hint: "Dây chuyền 01 & 02",
      badge: "Đang chạy",
      icon: Clock,
    },
  },
  {
    tone: "success",
    title: "4. Success (Xanh lá thành công)",
    ruleTitle: "Quy tắc: Hoàn tất / Đạt chuẩn QC / Đang hoạt động",
    ruleDesc: "Dùng cho mẻ hàng đóng gói hoàn tất, lô hàng kiểm định đạt QC, phiếu đã duyệt, thiết bị đang hoạt động tốt.",
    keywords: ["completed", "done", "hoàn thành", "thành công", "passed", "đạt", "active"],
    kpiDemo: {
      label: "Hoàn tất đóng thùng",
      value: "119",
      hint: "Đã nhập kho lạnh",
      badge: "Đạt QC",
      icon: CheckCircle2,
    },
  },
  {
    tone: "warning",
    title: "5. Warning (Cam vàng cảnh báo)",
    ruleTitle: "Quy tắc: Cảnh báo tồn / Tạm dừng / Sắp hết hạn",
    ruleDesc: "Cảnh báo tồn kho dưới ngưỡng an toàn, máy tạm dừng bảo dưỡng, hạn sử dụng dưới 7 ngày cần ưu tiên xuất trước.",
    keywords: ["warning", "paused", "atRisk", "tạm dừng", "cảnh báo", "gần hạn", "tồn thấp"],
    kpiDemo: {
      label: "Cảnh báo / Tồn thấp",
      value: "8",
      hint: "Cần tạo đơn đặt hàng",
      badge: "Gần hạn",
      icon: AlertTriangle,
    },
  },
  {
    tone: "danger",
    title: "6. Danger (Màu nóng: Đã hủy / Lỗi)",
    ruleTitle: "Quy tắc Vàng: Trạng thái đã hủy & Lỗi nghiêm trọng",
    ruleDesc: "Toàn bộ phiếu 'Đã hủy', 'Void', 'Từ chối' bắt buộc dùng màu nóng đỏ để báo hiệu tổn thất, gián đoạn hoặc hàng bị loại bỏ.",
    keywords: ["cancel", "cancelled", "void", "reject", "đã hủy", "hủy", "từ chối", "lỗi", "hỏng"],
    kpiDemo: {
      label: "Đã hủy / Bị từ chối",
      value: "3",
      hint: "Phiếu void do lệch cân",
      badge: "Đã hủy",
      icon: XCircle,
    },
  },
  {
    tone: "info",
    title: "7. Info (Xanh dương thông tin)",
    ruleTitle: "Quy tắc: Hướng dẫn SOP / Ghi chú nghiệp vụ",
    ruleDesc: "Dành cho các thông số hướng dẫn kỹ thuật, tài liệu quy trình vận hành, thông báo điều phối ca làm việc.",
    keywords: ["info", "notice", "hướng dẫn", "chỉ dẫn", "thông tin", "sop"],
    kpiDemo: {
      label: "Quy trình vận hành SOP",
      value: "12",
      hint: "Ca sáng áp dụng",
      badge: "Chỉ dẫn",
      icon: Info,
    },
  },
  {
    tone: "hold",
    title: "8. Hold (Tạm giữ QC / Khóa CCP)",
    ruleTitle: "Quy tắc: Niêm phong kiểm định & Vi phạm an toàn thực phẩm",
    ruleDesc: "Lô hàng bị chặn xuất, nghi ngờ vi sinh hoặc vượt điểm kiểm soát tới hạn CCP. Theo Design System, tone Hold kế thừa bảng màu Danger.",
    keywords: ["hold", "qa_hold", "ccp", "tạm giữ", "niêm phong", "khóa lô"],
    kpiDemo: {
      label: "Tạm giữ QC / Lỗi CCP",
      value: "2",
      hint: "Chờ mẫu xét nghiệm",
      badge: "Niêm phong",
      icon: ShieldAlert,
    },
  },
];

export function TestUiColorsSection() {
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>("primary");
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [previewSelectedKpi, setPreviewSelectedKpi] = useState<SemanticTone>("neutral");

  // Live Component Tester State
  const [testTone, setTestTone] = useState<SemanticTone>("neutral");
  const [testSize, setTestSize] = useState<"sm" | "md" | "lg">("md");
  const [testState, setTestState] = useState<"normal" | "active" | "disabled">("normal");
  const [bannerBorderStyle, setBannerBorderStyle] = useState<"same" | "contrast" | "accent-left">("same");

  const selectedFamily = COLOR_FAMILIES.find((f) => f.id === selectedFamilyId) ?? COLOR_FAMILIES[0]!;

  const handleCopy = (text: string, tokenKey: string) => {
    navigator.clipboard.writeText(text);
    setCopiedToken(tokenKey);
    setTimeout(() => setCopiedToken(null), 2500);
  };

  // Tone theme styles mapping for the Live Simulator
  const toneMap: Record<
    SemanticTone,
    {
      name: string;
      softBg: string;
      solidBg: string;
      hoverBg: string;
      textFg: string;
      border: string;
      softBorder: string;
      contrastBorder: string;
      accentLeftBorder: string;
      badgeTone: SemanticTone;
      icon: any;
      statusLabel: string;
      demoTitle: string;
      demoDesc: string;
    }
  > = {
    neutral: {
      name: "Neutral (Tổng quan / Chờ / Nháp)",
      softBg: "bg-bg",
      solidBg: "bg-text-secondary",
      hoverBg: "hover:bg-text",
      textFg: "text-text",
      border: "border-border",
      softBorder: "border-border/70",
      contrastBorder: "border-border-strong",
      accentLeftBorder: "border-transparent border-l-4 border-l-text-secondary",
      badgeTone: "neutral",
      icon: Layers,
      statusLabel: "Tất cả / Chờ / Nháp (Trung tính / Xám)",
      demoTitle: "Tổng Hợp Toàn Bộ Lệnh Vận Hành",
      demoDesc: "Số liệu tổng hoặc các phiếu khởi tạo ở trạng thái nháp đang chờ xử lý.",
    },
    primary: {
      name: "Primary (Brand)",
      softBg: "bg-primary-soft",
      solidBg: "bg-primary",
      hoverBg: "hover:bg-primary-hover",
      textFg: "text-primary-fg",
      border: "border-primary/40",
      softBorder: "border-primary-soft",
      contrastBorder: "border-primary/50",
      accentLeftBorder: "border-transparent border-l-4 border-l-primary",
      badgeTone: "primary",
      icon: Sparkles,
      statusLabel: "Khả dụng / Hành động chính",
      demoTitle: "Lệnh Sản Xuất MO-2026-001",
      demoDesc: "Dây chuyền Đóng gói STVQ đang vận hành ổn định.",
    },
    accent: {
      name: "Accent (Đang SX / WIP)",
      softBg: "bg-accent-soft",
      solidBg: "bg-accent",
      hoverBg: "hover:bg-lime-500",
      textFg: "text-accent-fg",
      border: "border-accent/40",
      softBorder: "border-accent-soft",
      contrastBorder: "border-accent/50",
      accentLeftBorder: "border-transparent border-l-4 border-l-accent",
      badgeTone: "accent",
      icon: Clock,
      statusLabel: "Đang sản xuất / Gom mẻ (WIP)",
      demoTitle: "Mẻ Nấu Syrup Đang Chạy",
      demoDesc: "Hệ thống đang kiểm soát nhiệt độ thanh trùng tại buồng gia nhiệt.",
    },
    success: {
      name: "Success (Thành công)",
      softBg: "bg-success-soft",
      solidBg: "bg-success",
      hoverBg: "hover:bg-emerald-600",
      textFg: "text-success-fg",
      border: "border-success/40",
      softBorder: "border-success-soft",
      contrastBorder: "border-success/50",
      accentLeftBorder: "border-transparent border-l-4 border-l-success",
      badgeTone: "success",
      icon: CheckCircle2,
      statusLabel: "Đạt chuẩn QC (Passed)",
      demoTitle: "Lô hàng RM-SUGAR-01 Đã Kiểm Định",
      demoDesc: "Toàn bộ chỉ tiêu vi sinh và độ ẩm đạt tiêu chuẩn ISO 22000.",
    },
    warning: {
      name: "Warning (Cảnh báo)",
      softBg: "bg-warning-soft",
      solidBg: "bg-warning",
      hoverBg: "hover:bg-amber-500",
      textFg: "text-warning-fg",
      border: "border-warning/40",
      softBorder: "border-warning-soft",
      contrastBorder: "border-warning/60",
      accentLeftBorder: "border-transparent border-l-4 border-l-warning",
      badgeTone: "warning",
      icon: AlertTriangle,
      statusLabel: "Cảnh báo / Sắp hết hạn",
      demoTitle: "Tồn Kho Đạt Ngưỡng Cảnh Báo",
      demoDesc: "Nguyên liệu Việt Quất IQF còn 280kg, cần tạo yêu cầu mua thêm.",
    },
    danger: {
      name: "Danger (Màu nóng: Đã hủy / Lỗi)",
      softBg: "bg-danger-soft",
      solidBg: "bg-danger",
      hoverBg: "hover:bg-danger-hover",
      textFg: "text-danger-fg",
      border: "border-danger/40",
      softBorder: "border-danger-soft",
      contrastBorder: "border-danger/50",
      accentLeftBorder: "border-transparent border-l-4 border-l-danger",
      badgeTone: "danger",
      icon: XCircle,
      statusLabel: "Đã hủy / Bị từ chối / Lỗi",
      demoTitle: "Lệnh Đã Bị Hủy Bỏ (Void)",
      demoDesc: "Lệnh đã bị void do thay đổi kế hoạch sản xuất ca đột xuất.",
    },
    info: {
      name: "Info (Thông tin)",
      softBg: "bg-info-soft",
      solidBg: "bg-info",
      hoverBg: "hover:bg-sky-400",
      textFg: "text-info-fg",
      border: "border-info/40",
      softBorder: "border-info-soft",
      contrastBorder: "border-info/50",
      accentLeftBorder: "border-transparent border-l-4 border-l-info",
      badgeTone: "info",
      icon: Info,
      statusLabel: "Bản ghi nháp / Hướng dẫn",
      demoTitle: "Hướng Dẫn Vận Hành Ca Đêm",
      demoDesc: "Kiểm tra khử trùng CIP trước khi tiếp nhận mẻ nguyên liệu mới.",
    },
    hold: {
      name: "Hold (Tạm giữ QC / Khóa CCP)",
      softBg: "bg-danger-soft",
      solidBg: "bg-danger",
      hoverBg: "hover:bg-danger-hover",
      textFg: "text-danger-fg",
      border: "border-danger/40",
      softBorder: "border-danger-soft",
      contrastBorder: "border-danger/50",
      accentLeftBorder: "border-transparent border-l-4 border-l-danger",
      badgeTone: "hold",
      icon: ShieldAlert,
      statusLabel: "Khóa Lô Hàng / Lỗi CCP",
      demoTitle: "Lô SFG-PREMIX-02 Bị Niêm Phong Tạm Giữ",
      demoDesc: "Nhiệt độ phòng lạnh vượt ngưỡng cho phép trong 35 phút.",
    },
    draft: {
      name: "Draft (Nháp & Chờ duyệt)",
      softBg: "bg-slate-100",
      solidBg: "bg-slate-100",
      hoverBg: "hover:bg-slate-200/70",
      textFg: "text-slate-700",
      border: "border-slate-300",
      softBorder: "border-slate-200",
      contrastBorder: "border-slate-400",
      accentLeftBorder: "border-transparent border-l-4 border-l-slate-400",
      badgeTone: "draft",
      icon: Layers,
      statusLabel: "Nháp / Chờ duyệt / Chờ sx / Tạm lưu (Light Gray)",
      demoTitle: "Bản Ghi Lưu Tạm / Chờ Phê Duyệt",
      demoDesc: "Hồ sơ đang ở trạng thái soạn thảo ban đầu, chờ chuyển sang sản xuất.",
    },
  };

  const currentToneConfig = toneMap[testTone];
  const ToneIcon = currentToneConfig.icon;

  return (
    <div className="space-y-8">
      {/* 0. Bảng Thống Nhất Màu Sắc Toàn Hệ Thống (Master Color Matrix) */}
      <TestUiUnifiedStatusMatrix />

      {/* 1. Header & Giới thiệu triết lý */}
      <div className="rounded-control border border-border bg-surface p-5 sm:p-6 shadow-xs">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex size-9 items-center justify-center rounded-lg bg-primary-soft text-primary shadow-xs">
                <Palette className="size-5" />
              </span>
              <h2 className="m-0 text-h2 font-bold text-text">
                Quy Chuẩn Màu Sắc & Thang Độ Đậm Nhạt (Design Color Tokens)
              </h2>
            </div>
            <p className="m-0 mt-1.5 text-body text-text-secondary max-w-3xl">
              Hệ thống màu OKLCH chuẩn hóa cho TM Food ERP: Đảm bảo phân cấp thị giác 4 bậc (Nền nhạt Soft → Viền Border → Nền nhấn Solid → Chữ tương phản Foreground), chống chói mắt dưới ánh đèn nhà xưởng và tuân thủ tuyệt đối chuẩn tương phản WCAG AA/AAA.
            </p>
          </div>

          <div className="flex items-center gap-2 self-start md:self-auto">
            <span className="rounded-full bg-primary-soft px-3 py-1 text-caption font-semibold text-primary">
              OKLCH Color Space
            </span>
            <span className="rounded-full bg-surface-muted px-3 py-1 text-caption font-medium text-text-secondary border border-border">
              WCAG AAA Ready
            </span>
          </div>
        </div>

        {/* 4 Bậc sắc độ chuẩn */}
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-card border border-border/70 bg-bg p-3.5 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-caption font-bold text-text">Bậc 1: Nền Nhạt (Soft)</span>
              <span className="text-[11px] font-mono text-text-muted">L: 93% - 95%</span>
            </div>
            <p className="m-0 text-caption text-text-secondary leading-relaxed">
              Dùng làm nền Badge, Banner, thẻ highlight. Màu phấn dịu mắt, không gây phân tâm.
            </p>
            <div className="pt-1 flex items-center gap-1.5 font-mono text-[11px] text-text-muted">
              <code>bg-*-soft</code>
            </div>
          </div>

          <div className="rounded-card border border-border/70 bg-bg p-3.5 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-caption font-bold text-text">Bậc 2: Đường Viền (Border)</span>
              <span className="text-[11px] font-mono text-text-muted">L: 85% - 90%</span>
            </div>
            <p className="m-0 text-caption text-text-secondary leading-relaxed">
              Phân chia ranh giới card, ô input, phân cách hàng bảng mà không tạo cảm giác gãy vụn.
            </p>
            <div className="pt-1 flex items-center gap-1.5 font-mono text-[11px] text-text-muted">
              <code>border-border</code>
            </div>
          </div>

          <div className="rounded-card border border-border/70 bg-bg p-3.5 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-caption font-bold text-text">Bậc 3: Nhấn Mạnh (Solid)</span>
              <span className="text-[11px] font-mono text-text-muted">L: 42% - 68%</span>
            </div>
            <p className="m-0 text-caption text-text-secondary leading-relaxed">
              Màu đại diện chính cho hành động. Nút bấm CTA, dot active, icon nổi bật.
            </p>
            <div className="pt-1 flex items-center gap-1.5 font-mono text-[11px] text-text-muted">
              <code>bg-primary</code>
            </div>
          </div>

          <div className="rounded-card border border-border/70 bg-bg p-3.5 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-caption font-bold text-text">Bậc 4: Chữ Tương Phản (Fg)</span>
              <span className="text-[11px] font-mono text-text-muted">L: 28% - 42%</span>
            </div>
            <p className="m-0 text-caption text-text-secondary leading-relaxed">
              Màu chữ và icon hiển thị trên nền Soft. Đảm bảo độ tương phản vượt trội ≥ 7:1.
            </p>
            <div className="pt-1 flex items-center gap-1.5 font-mono text-[11px] text-text-muted">
              <code>text-*-fg</code>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Interactive Live Tester - "CÓ UI TEST MÀU XEM LUÔN" */}
      <div className="rounded-control border-2 border-primary/20 bg-surface p-5 sm:p-6 shadow-sm space-y-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between border-b border-border/80 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <Eye className="size-5 text-primary" />
              <h3 className="m-0 text-h3 font-bold text-text">
                UI Test Màu Trực Quan (Interactive Live Component Simulator)
              </h3>
              <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-bold text-emerald-800">
                Live Test
              </span>
            </div>
            <p className="m-0 mt-0.5 text-caption text-text-secondary">
              Bấm chọn gam màu và trạng thái bên dưới để kiểm tra trực quan độ hòa hợp trên các component chuẩn của hệ thống.
            </p>
          </div>

          {/* Selector Tone */}
          <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border bg-bg p-1">
            {([
              { tone: "neutral", label: "Neutral (Tổng/Nháp)", dotClass: "bg-text-secondary" },
              { tone: "primary", label: "Primary (Brand)", dotClass: "bg-primary" },
              { tone: "accent", label: "Accent (WIP)", dotClass: "bg-accent" },
              { tone: "success", label: "Success (Đạt)", dotClass: "bg-success" },
              { tone: "warning", label: "Warning (Cảnh báo)", dotClass: "bg-warning" },
              { tone: "danger", label: "Danger (Đã hủy)", dotClass: "bg-danger" },
              { tone: "info", label: "Info (Thông tin)", dotClass: "bg-info" },
              { tone: "hold", label: "Hold (Tạm giữ)", dotClass: "bg-danger" },
            ] as const).map(({ tone: t, label, dotClass }) => (
              <button
                key={t}
                type="button"
                onClick={() => setTestTone(t)}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-caption font-medium transition-all cursor-pointer",
                  testTone === t
                    ? "bg-surface text-text font-bold shadow-xs border border-border/80"
                    : "text-text-secondary hover:text-text"
                )}
              >
                <span className={cn("size-2.5 rounded-full shrink-0", dotClass)} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Khung Test Component Trực Tiếp */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Cột trái: Các thành phần UI áp dụng màu (7 cols) */}
          <div className="space-y-4 lg:col-span-7">
            <div className="text-caption font-semibold uppercase tracking-wider text-text-muted">
              1. Hiển thị thực tế trên các thành phần (Component Previews)
            </div>

            {/* A. Banner / Notice Block */}
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-caption font-semibold text-text">
                  A. Banner / Thẻ Thông Báo (Kiểm nghiệm Viền & Nền)
                </span>
                {/* Switcher */}
                <div className="flex items-center gap-1 rounded-md border border-border bg-bg p-0.5 text-[11px]">
                  <button
                    type="button"
                    onClick={() => setBannerBorderStyle("same")}
                    className={cn(
                      "rounded px-2.5 py-1 font-medium transition-colors cursor-pointer",
                      bannerBorderStyle === "same"
                        ? "bg-surface font-bold text-text shadow-xs"
                        : "text-text-secondary hover:text-text"
                    )}
                  >
                    ✨ Nền & Viền cùng màu
                  </button>
                  <button
                    type="button"
                    onClick={() => setBannerBorderStyle("contrast")}
                    className={cn(
                      "rounded px-2.5 py-1 font-medium transition-colors cursor-pointer",
                      bannerBorderStyle === "contrast"
                        ? "bg-surface font-bold text-text shadow-xs"
                        : "text-text-secondary hover:text-text"
                    )}
                  >
                    Viền tương phản (Kiểu cũ)
                  </button>
                  <button
                    type="button"
                    onClick={() => setBannerBorderStyle("accent-left")}
                    className={cn(
                      "rounded px-2.5 py-1 font-medium transition-colors cursor-pointer",
                      bannerBorderStyle === "accent-left"
                        ? "bg-surface font-bold text-text shadow-xs"
                        : "text-text-secondary hover:text-text"
                    )}
                  >
                    Nhấn cạnh trái (border-l-4)
                  </button>
                </div>
              </div>

              {/* Live Selected Banner */}
              <div
                className={cn(
                  "flex items-start gap-3 rounded-card border p-4 transition-all duration-200 shadow-2xs",
                  currentToneConfig.softBg,
                  bannerBorderStyle === "same"
                    ? currentToneConfig.softBorder
                    : bannerBorderStyle === "contrast"
                    ? currentToneConfig.contrastBorder
                    : currentToneConfig.accentLeftBorder
                )}
              >
                <ToneIcon className={cn("size-5 shrink-0 mt-0.5", currentToneConfig.textFg)} />
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h4 className={cn("m-0 text-body font-bold", currentToneConfig.textFg)}>
                      {currentToneConfig.demoTitle}
                    </h4>
                    <span
                      className={cn(
                        "rounded px-2 py-0.5 font-mono text-[11px] font-semibold border",
                        bannerBorderStyle === "same"
                          ? `${currentToneConfig.softBorder} ${currentToneConfig.textFg} bg-surface/50`
                          : bannerBorderStyle === "contrast"
                          ? `${currentToneConfig.contrastBorder} ${currentToneConfig.textFg} bg-surface/50`
                          : "border-border text-text bg-surface"
                      )}
                    >
                      {bannerBorderStyle === "same"
                        ? "Đang chọn: Nền & Viền chung màu (border-*-soft)"
                        : bannerBorderStyle === "contrast"
                        ? "Đang chọn: Viền tương phản (border-*)"
                        : "Đang chọn: Nhấn cạnh trái (border-l-4)"}
                    </span>
                  </div>
                  <p className={cn("m-0 text-caption opacity-90 leading-relaxed", currentToneConfig.textFg)}>
                    {currentToneConfig.demoDesc}
                  </p>
                </div>
              </div>

              {/* Visual Side-by-Side Comparison Box */}
              <div className="rounded-card border border-border/80 bg-surface/60 p-3.5 space-y-2.5">
                <div className="text-[12px] font-semibold text-text-secondary">
                  So sánh trực quan 3 phương án đường viền (Border Treatment):
                </div>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                  {/* Option 1: Same Color */}
                  <div
                    onClick={() => setBannerBorderStyle("same")}
                    className={cn(
                      "cursor-pointer rounded-md border p-2.5 transition-all",
                      currentToneConfig.softBg,
                      currentToneConfig.softBorder,
                      bannerBorderStyle === "same" ? "ring-2 ring-primary ring-offset-1" : "hover:opacity-90"
                    )}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <ToneIcon className={cn("size-3.5", currentToneConfig.textFg)} />
                      <span className={cn("text-[11px] font-bold", currentToneConfig.textFg)}>1. Nền & Viền chung màu</span>
                    </div>
                    <p className={cn("text-[10px] m-0 opacity-80 leading-tight", currentToneConfig.textFg)}>
                      Dịu mắt, liền mạch, hiện đại, không bị viền sẫm đóng hộp.
                    </p>
                  </div>

                  {/* Option 2: Contrast Border */}
                  <div
                    onClick={() => setBannerBorderStyle("contrast")}
                    className={cn(
                      "cursor-pointer rounded-md border p-2.5 transition-all",
                      currentToneConfig.softBg,
                      currentToneConfig.contrastBorder,
                      bannerBorderStyle === "contrast" ? "ring-2 ring-primary ring-offset-1" : "hover:opacity-90"
                    )}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <ToneIcon className={cn("size-3.5", currentToneConfig.textFg)} />
                      <span className={cn("text-[11px] font-bold", currentToneConfig.textFg)}>2. Viền tương phản</span>
                    </div>
                    <p className={cn("text-[10px] m-0 opacity-80 leading-tight", currentToneConfig.textFg)}>
                      Đường viền đậm bao quanh, tạo ranh giới nét nhưng hơi cứng.
                    </p>
                  </div>

                  {/* Option 3: Left Accent */}
                  <div
                    onClick={() => setBannerBorderStyle("accent-left")}
                    className={cn(
                      "cursor-pointer rounded-md border p-2.5 transition-all",
                      currentToneConfig.softBg,
                      currentToneConfig.accentLeftBorder,
                      bannerBorderStyle === "accent-left" ? "ring-2 ring-primary ring-offset-1" : "hover:opacity-90"
                    )}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <ToneIcon className={cn("size-3.5", currentToneConfig.textFg)} />
                      <span className={cn("text-[11px] font-bold", currentToneConfig.textFg)}>3. Nhấn cạnh trái</span>
                    </div>
                    <p className={cn("text-[10px] m-0 opacity-80 leading-tight", currentToneConfig.textFg)}>
                      Chỉ viền dày bên trái làm điểm neo, 3 cạnh còn lại chìm.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* B. Badges & Status Pills */}
            <div className="rounded-card border border-border bg-surface p-4 space-y-3">
              <span className="text-caption font-medium text-text-secondary">Trạng thái & Nhãn (Badges & Pills)</span>
              <div className="flex flex-wrap items-center gap-3">
                {/* Standard Badge */}
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-caption font-semibold",
                    currentToneConfig.softBg,
                    currentToneConfig.textFg
                  )}
                >
                  <span className={cn("size-2 rounded-full", currentToneConfig.solidBg)} />
                  {currentToneConfig.statusLabel}
                </span>

                {/* Solid Badge */}
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-caption font-semibold text-white",
                    currentToneConfig.solidBg
                  )}
                >
                  <ToneIcon className="size-3.5" />
                  <span>Ưu tiên cao</span>
                </span>

                {/* Outline Badge */}
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-caption font-medium",
                    currentToneConfig.border,
                    currentToneConfig.textFg
                  )}
                >
                  <span>Mã kiểm soát CCP-01</span>
                </span>
              </div>
            </div>

            {/* C. Action Buttons */}
            <div className="rounded-card border border-border bg-surface p-4 space-y-3">
              <span className="text-caption font-medium text-text-secondary">Nút hành động (Buttons)</span>
              <div className="flex flex-wrap items-center gap-3">
                {/* Solid Button */}
                <button
                  type="button"
                  className={cn(
                    "inline-flex h-9 items-center gap-2 rounded-md px-4 text-caption font-semibold text-white transition-all shadow-xs",
                    currentToneConfig.solidBg,
                    currentToneConfig.hoverBg
                  )}
                >
                  <ToneIcon className="size-4" />
                  <span>Hành động chính</span>
                </button>

                {/* Soft Button */}
                <button
                  type="button"
                  className={cn(
                    "inline-flex h-9 items-center gap-2 rounded-md px-4 text-caption font-semibold transition-all",
                    currentToneConfig.softBg,
                    currentToneConfig.textFg,
                    "hover:brightness-95"
                  )}
                >
                  <span>Nút mềm (Soft)</span>
                </button>

                {/* Outline Button */}
                <button
                  type="button"
                  className={cn(
                    "inline-flex h-9 items-center gap-2 rounded-md border px-4 text-caption font-medium transition-all bg-surface hover:bg-bg",
                    currentToneConfig.border,
                    currentToneConfig.textFg
                  )}
                >
                  <span>Đường viền (Outline)</span>
                </button>
              </div>
            </div>

            {/* D. Data Table Row Highlight */}
            <div className="rounded-card border border-border overflow-hidden">
              <div className="bg-bg/80 px-3.5 py-2 text-caption font-bold text-text-secondary border-b border-border">
                Mô phỏng hàng dữ liệu DataTable
              </div>
              <div className={cn("flex items-center justify-between p-3.5 transition-colors", currentToneConfig.softBg, currentToneConfig.accentLeftBorder)}>
                <div className="flex items-center gap-3">
                  <span className={cn("flex size-7 items-center justify-center rounded-full text-white", currentToneConfig.solidBg)}>
                    <ToneIcon className="size-4" />
                  </span>
                  <div>
                    <div className="text-body font-bold text-text">MO-2026-0713-001 (STVQ)</div>
                    <div className="text-caption text-text-secondary">Chuyền 01 · Ca 1 · 1.500 Thùng</div>
                  </div>
                </div>
                <span className={cn("rounded-md px-2.5 py-1 text-caption font-semibold", currentToneConfig.softBg, currentToneConfig.textFg)}>
                  {currentToneConfig.statusLabel}
                </span>
              </div>
            </div>
          </div>

          {/* Cột phải: Thông số Kỹ thuật & Độ Tương Phản (5 cols) */}
          <div className="space-y-4 lg:col-span-5">
            <div className="text-caption font-semibold uppercase tracking-wider text-text-muted">
              2. Đánh giá kỹ thuật & Tương phản (Contrast Audit)
            </div>

            <div className="rounded-card border border-border bg-bg p-4 space-y-4">
              {/* Sắc độ hiện hành */}
              <div className="space-y-2">
                <div className="text-caption font-bold text-text flex items-center justify-between">
                  <span>Màu đang chọn:</span>
                  <span className="font-mono text-primary font-semibold">{currentToneConfig.name}</span>
                </div>
                <div className="flex items-center gap-2 rounded-md border border-border/80 bg-surface p-2.5">
                  <div className={cn("size-8 rounded shadow-xs", currentToneConfig.solidBg)} />
                  <div className="min-w-0 flex-1 text-caption">
                    <div className="font-mono font-bold text-text">var(--color-{testTone})</div>
                    <div className="text-text-muted truncate">Base Accent Token</div>
                  </div>
                </div>
              </div>

              {/* Tương phản WCAG Checker */}
              <div className="rounded-md border border-border/80 bg-surface p-3.5 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-caption font-bold text-text">Kiểm Chuẩn WCAG Tương Phản</span>
                  <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800">
                    ĐẠT CHUẨN AAA
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-caption">
                  <div className="rounded bg-bg p-2 text-center">
                    <div className="text-text-muted text-[11px]">Chữ trên nền Soft</div>
                    <div className="font-mono font-bold text-emerald-700 text-body">≥ 7.2 : 1</div>
                    <div className="text-[10px] text-text-secondary">Rõ nét dưới ánh sáng xưởng</div>
                  </div>
                  <div className="rounded bg-bg p-2 text-center">
                    <div className="text-text-muted text-[11px]">Chữ trắng trên Solid</div>
                    <div className="font-mono font-bold text-emerald-700 text-body">≥ 4.8 : 1</div>
                    <div className="text-[10px] text-text-secondary">Đạt chuẩn AA nút bấm</div>
                  </div>
                </div>
              </div>

              {/* Copy Token Nhanh */}
              <div className="space-y-1.5">
                <span className="text-caption font-medium text-text-secondary">Mã Tailwind CSS để gắn trực tiếp:</span>
                <div className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-caption font-mono">
                  <span className="text-primary truncate">
                    {currentToneConfig.softBg} {currentToneConfig.textFg}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopy(`${currentToneConfig.softBg} ${currentToneConfig.textFg}`, "quick-class")}
                    className="ml-2 text-text-muted hover:text-text cursor-pointer"
                    title="Sao chép"
                  >
                    {copiedToken === "quick-class" ? (
                      <Check className="size-4 text-emerald-600" />
                    ) : (
                      <Copy className="size-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Nguyên tắc bất di bất dịch */}
              <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-3 text-caption text-amber-900 space-y-1">
                <div className="flex items-center gap-1.5 font-bold">
                  <ShieldAlert className="size-4 text-amber-700" />
                  <span>Quy tắc Vàng cho FE:</span>
                </div>
                <p className="m-0 leading-relaxed text-[12px]">
                  <strong>Tuyệt đối không</strong> dùng màu chữ thường <code>text-text</code> trên nền <code>bg-*-soft</code>. Phải luôn đi cặp: Nền nhạt <code>bg-*-soft</code> thì Chữ phải là <code>text-*-fg</code>!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Quy Chuẩn Màu Cho CardKPI & Card Tổng Quan Mobile (8 Tone Màu Chuẩn) */}
      <div className="rounded-control border-2 border-primary/20 bg-surface p-5 sm:p-6 shadow-sm space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-border/80 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex size-9 items-center justify-center rounded-lg bg-primary-soft text-primary shadow-xs">
                <Sliders className="size-5" />
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="m-0 text-h3 font-bold text-text">
                    Quy Chuẩn 8 Tone Màu Cho CardKPI & Card Tổng Quan Mobile
                  </h3>
                  <span className="rounded-full bg-primary-soft px-2.5 py-0.5 text-[11px] font-bold text-primary">
                    Quy Tắc Vàng
                  </span>
                </div>
                <p className="m-0 mt-0.5 text-caption text-text-secondary">
                  Thống nhất nhận diện màu trạng thái và số liệu giữa Desktop (<code>KpiCard</code>, <code>buildListInsights</code>) và Mobile (<code>FieldTodayKpiStrip</code>, <code>MOBILE_KPI_TILE_STYLES</code>).
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-caption text-text-muted">Bấm chọn thẻ để test trạng thái Active</span>
          </div>
        </div>

        {/* 3 Quy tắc cốt lõi */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-card border-2 border-border bg-bg p-4 space-y-1.5 shadow-2xs">
            <div className="flex items-center gap-2 text-text">
              <span className="flex size-6 items-center justify-center rounded-full bg-surface border border-border text-[12px] font-bold">1</span>
              <span className="text-caption font-bold">Tất cả / Tổng: Màu Trung Tính</span>
            </div>
            <p className="m-0 text-caption text-text-secondary leading-relaxed">
              Màu của tất cả, tổng số là <strong>màu trung tính (neutral)</strong>. Không dùng màu nóng đỏ hay màu xanh nổi bật để tránh áp đặt thiên kiến số liệu.
            </p>
            <div className="pt-1">
              <Badge tone="neutral" label="Tone: neutral" />
            </div>
          </div>

          <div className="rounded-card border-2 border-danger/30 bg-danger-soft/40 p-4 space-y-1.5 shadow-2xs">
            <div className="flex items-center gap-2 text-danger-fg">
              <span className="flex size-6 items-center justify-center rounded-full bg-danger text-white text-[12px] font-bold">2</span>
              <span className="text-caption font-bold">Status Đã Hủy: Màu Nóng</span>
            </div>
            <p className="m-0 text-caption text-danger-fg/90 leading-relaxed">
              Màu status đã hủy, hủy bỏ, void, từ chối bắt buộc là <strong>màu nóng (danger)</strong> nhằm báo động ngay hao hụt, sự cố hoặc gián đoạn quy trình.
            </p>
            <div className="pt-1">
              <Badge tone="danger" label="Tone: danger" />
            </div>
          </div>

          <div className="rounded-card border-2 border-border bg-bg p-4 space-y-1.5 shadow-2xs">
            <div className="flex items-center gap-2 text-text">
              <span className="flex size-6 items-center justify-center rounded-full bg-surface border border-border text-[12px] font-bold">3</span>
              <span className="text-caption font-bold">Chờ / Nháp: Màu Xám</span>
            </div>
            <p className="m-0 text-caption text-text-secondary leading-relaxed">
              Màu chờ duyệt, nháp, tạm lưu, đang tắt là <strong>màu xám (neutral)</strong> dịu mắt, tránh gây hiểu nhầm sang lỗi nguy cấp hay cảnh báo cao.
            </p>
            <div className="pt-1">
              <Badge tone="neutral" label="Tone: neutral" />
            </div>
          </div>
        </div>

        {/* Ma trận 8 Tone: KpiCard Desktop & Mobile Tile Preview */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-caption font-semibold uppercase tracking-wider text-text-muted">
              Chi tiết hiển thị 8 Tone Ngữ Nghĩa (Bấm vào Card để xem hiệu ứng Active):
            </div>
            <div className="text-caption font-mono text-text-secondary">
              Đang chọn KpiCard: <span className="font-bold text-primary">{previewSelectedKpi}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {SEMANTIC_TONE_RULES.map((rule) => {
              const isSelected = previewSelectedKpi === rule.tone;
              const mobileStyle = MOBILE_KPI_TILE_STYLES[rule.tone];
              const resolvedTone = resolveKpiTone({ label: rule.kpiDemo.label, tone: rule.tone });

              return (
                <div
                  key={rule.tone}
                  className={cn(
                    "flex flex-col justify-between rounded-control border bg-surface p-4 transition-all duration-200 space-y-3.5",
                    isSelected
                      ? "border-primary shadow-md ring-2 ring-primary/20"
                      : "border-border hover:border-border-strong hover:shadow-xs"
                  )}
                >
                  {/* Card Header */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-body font-bold text-text truncate">
                        {rule.title}
                      </span>
                      <Badge tone={rule.tone} label={rule.tone} />
                    </div>
                    <p className="m-0 text-[12px] text-text-secondary leading-snug line-clamp-2">
                      {rule.ruleDesc}
                    </p>
                  </div>

                  {/* 1. Desktop KpiCard Preview */}
                  <div className="space-y-1">
                    <div className="text-[11px] font-semibold text-text-muted flex items-center justify-between">
                      <span>Desktop KpiCard</span>
                      <span className="text-[10px] text-primary">{isSelected ? "● Đang chọn" : "Bấm để chọn"}</span>
                    </div>
                    <KpiCard
                      item={{
                        id: `demo-${rule.tone}`,
                        label: rule.kpiDemo.label,
                        value: rule.kpiDemo.value,
                        hint: rule.kpiDemo.hint,
                        badge: rule.kpiDemo.badge,
                        icon: rule.kpiDemo.icon,
                        tone: rule.tone,
                        selected: isSelected,
                        onClick: () => setPreviewSelectedKpi(rule.tone),
                      }}
                    />
                  </div>

                  {/* 2. Mobile Overview Tile Preview */}
                  <div className="space-y-1">
                    <div className="text-[11px] font-semibold text-text-muted">
                      Mobile Overview Tile (Chuẩn mobile)
                    </div>
                    <div className="rounded-lg border border-border/80 bg-surface p-3 space-y-1.5 shadow-2xs">
                      <div className="flex items-center justify-between">
                        <span className={cn("rounded px-2 py-0.5 text-[11px] font-semibold", mobileStyle.badge)}>
                          {rule.kpiDemo.badge}
                        </span>
                        <span className="text-[10px] text-text-muted font-mono">mobile-strip</span>
                      </div>
                      <div className={cn("text-h2 font-bold font-mono", mobileStyle.value)}>
                        {rule.kpiDemo.value}
                      </div>
                      <div className="text-[11px] text-text-secondary truncate">
                        {rule.kpiDemo.label}
                      </div>
                    </div>
                  </div>

                  {/* 3. Badges & Auto-Resolver Test */}
                  <div className="pt-2 border-t border-border/70 space-y-2">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-text-muted">Badges:</span>
                      <div className="flex items-center gap-1.5">
                        <Badge tone={rule.tone} label={rule.kpiDemo.badge} />
                      </div>
                    </div>

                    <div className="rounded bg-bg p-2 text-[11px] font-mono text-text-secondary flex items-center justify-between">
                      <span className="truncate max-w-[140px]">resolveKpiTone()</span>
                      <span className="font-bold text-primary">➔ "{resolvedTone}"</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 4. Danh mục Chi tiết các Thang Màu trong hệ thống (The Complete Matrix) */}
      <div className="rounded-control border border-border bg-surface p-5 sm:p-6 shadow-xs space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-border/80 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <Layers className="size-5 text-primary" />
              <h3 className="m-0 text-h3 font-bold text-text">
                Bảng Ma Trận Các Sắc Tộc Màu (Design Token Library)
              </h3>
            </div>
            <p className="m-0 text-caption text-text-secondary">
              Xem chi tiết mã biến CSS, giá trị OKLCH chuẩn và phạm vi sử dụng được quy định trong <code>src/styles/tokens.css</code>.
            </p>
          </div>

          {/* Family Tabs */}
          <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-bg p-1">
            {COLOR_FAMILIES.map((fam) => (
              <button
                key={fam.id}
                type="button"
                onClick={() => setSelectedFamilyId(fam.id)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-caption font-semibold transition-all cursor-pointer",
                  selectedFamilyId === fam.id
                    ? "bg-surface text-primary shadow-xs border border-border/70"
                    : "text-text-secondary hover:text-text"
                )}
              >
                {fam.name.split(" ")[0]}
              </button>
            ))}
          </div>
        </div>

        {/* Selected Family Details */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2 bg-bg p-3.5 rounded-lg border border-border/60">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-body font-bold text-text">{selectedFamily.name}</span>
                <span className="text-caption text-text-muted">({selectedFamily.vietnameseName})</span>
              </div>
              <p className="m-0 mt-0.5 text-caption text-text-secondary">{selectedFamily.description}</p>
            </div>
            <div className="font-mono text-caption text-text-muted">Tọa độ góc: {selectedFamily.hue}</div>
          </div>

          {/* Tokens Grid */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {selectedFamily.tokens.map((tok) => {
              const copyKey = `${tok.variable}`;
              return (
                <div
                  key={tok.variable}
                  className="group relative flex flex-col justify-between rounded-card border border-border bg-surface p-3.5 transition-all hover:border-primary/50 hover:shadow-xs"
                >
                  <div className="space-y-3">
                    {/* Color Swatch */}
                    <div
                      className="h-16 w-full rounded-md border border-black/10 flex items-center justify-center p-2 text-center transition-transform group-hover:scale-[1.01]"
                      style={{ backgroundColor: tok.previewBg }}
                    >
                      <span
                        className="text-caption font-mono font-bold px-2 py-0.5 rounded shadow-xs"
                        style={{ color: tok.textColor }}
                      >
                        {tok.cssValue}
                      </span>
                    </div>

                    {/* Metadata */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-caption font-bold text-text">{tok.name}</span>
                        <span className="text-[11px] font-semibold text-text-muted bg-bg px-2 py-0.5 rounded">
                          {tok.role}
                        </span>
                      </div>
                      <p className="m-0 text-[12px] text-text-secondary leading-snug line-clamp-2">
                        {tok.usage}
                      </p>
                    </div>
                  </div>

                  {/* Copy Footer */}
                  <div className="mt-3 pt-2.5 border-t border-border/60 flex items-center justify-between text-caption font-mono">
                    <span className="text-[11px] text-text-muted truncate max-w-[180px]">
                      {tok.variable}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleCopy(tok.variable, copyKey)}
                      className="flex items-center gap-1 text-[11px] font-sans font-semibold text-primary hover:underline cursor-pointer"
                    >
                      {copiedToken === copyKey ? (
                        <>
                          <Check className="size-3 text-emerald-600" />
                          <span>Đã chép</span>
                        </>
                      ) : (
                        <>
                          <Copy className="size-3" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 5. Quy tắc Kiểm tra & Checklist dành cho Developer / UI Designer */}
      <div className="rounded-control border border-border bg-surface p-5 sm:p-6 shadow-xs space-y-4">
        <div className="flex items-center gap-2 border-b border-border/80 pb-3">
          <CheckCircle2 className="size-5 text-emerald-600" />
          <h3 className="m-0 text-h3 font-bold text-text">
            Checklist Kiểm Định Màu Sắc Trước Khi Triển Khai (UI QA Criteria)
          </h3>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="flex items-start gap-2.5 rounded-lg border border-border/70 bg-bg p-3">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
              1
            </span>
            <div className="text-caption">
              <span className="font-bold text-text">Không dùng mã màu Hex/RGB thô:</span>
              <p className="m-0 text-text-secondary mt-0.5">
                Mọi class màu bắt buộc sử dụng CSS variables trong Design System (ví dụ: <code>text-primary-fg</code> thay vì <code>#166534</code>).
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2.5 rounded-lg border border-border/70 bg-bg p-3">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
              2
            </span>
            <div className="text-caption">
              <span className="font-bold text-text">Thẻ không hoạt động (Inactive/Disabled):</span>
              <p className="m-0 text-text-secondary mt-0.5">
                Bắt buộc dùng tone Neutral xám (<code>bg-disabled-bg text-text-disabled</code>), không dùng màu cam rực rỡ hay màu tươi.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2.5 rounded-lg border border-border/70 bg-bg p-3">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
              3
            </span>
            <div className="text-caption">
              <span className="font-bold text-text">Quy ước Khóa Lô (HOLD / CCP):</span>
              <p className="m-0 text-text-secondary mt-0.5">
                Luôn trỏ trực tiếp về biến Semantic Danger (<code>--color-danger</code>), không tạo thêm màu vàng đất hay màu nâu riêng biệt.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2.5 rounded-lg border border-border/70 bg-bg p-3">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
              4
            </span>
            <div className="text-caption">
              <span className="font-bold text-text">Hỗ trợ người mù màu (Color Blindness):</span>
              <p className="m-0 text-text-secondary mt-0.5">
                Mọi trạng thái có màu (Đạt/Không đạt, Xong/Lỗi) phải luôn đi kèm chữ (Label) hoặc biểu tượng (Icon Check/X) tương ứng.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TestUiColorsSection;
